import type { TripRecommendation } from '@/services/aiPlanner';
import { createItineraryItem } from '@/services/itinerary';
import { buildItineraryItemsFromHighlights } from '@/services/recommendationItineraryParser';

export type { ParsedItineraryHighlight, RecommendationItineraryItemDraft } from '@/services/recommendationItineraryParser';
export { buildItineraryItemsFromHighlights, clampItineraryHighlightsToDuration, parseItineraryHighlight } from '@/services/recommendationItineraryParser';

export type CreateItineraryFromRecommendationResult = {
  createdCount: number;
  attemptedCount: number;
  errors: Array<{ title: string; error: unknown }>;
};

/**
 * Materialize Co-Pilot `itineraryHighlights` into the trip's plan tab itinerary.
 */
export async function createItineraryFromRecommendationHighlights(args: {
  tripId: string;
  userId: string;
  recommendation: Pick<TripRecommendation, 'itineraryHighlights' | 'destination' | 'whyThisMatch' | 'duration'>;
  tripStartDate: Date;
}): Promise<CreateItineraryFromRecommendationResult> {
  const highlights = args.recommendation.itineraryHighlights?.filter((line) => line?.trim()) ?? [];
  if (highlights.length === 0) {
    return { createdCount: 0, attemptedCount: 0, errors: [] };
  }

  const durationDays =
    typeof args.recommendation.duration === 'number' && args.recommendation.duration > 0
      ? args.recommendation.duration
      : undefined;

  const items = buildItineraryItemsFromHighlights({
    highlights,
    tripStartDate: args.tripStartDate,
    userId: args.userId,
    destination: args.recommendation.destination,
    durationDays,
  });

  const errors: Array<{ title: string; error: unknown }> = [];
  let createdCount = 0;

  for (const item of items) {
    try {
      await createItineraryItem(args.tripId, item);
      createdCount += 1;
    } catch (error) {
      errors.push({ title: item.title, error });
      console.error('[RecommendationItinerary] Failed to create itinerary item', {
        tripId: args.tripId,
        title: item.title,
        error,
      });
    }
  }

  if (errors.length > 0) {
    console.warn('[RecommendationItinerary] Completed with partial failures', {
      tripId: args.tripId,
      createdCount,
      attemptedCount: items.length,
      errors: errors.length,
    });
  }

  return {
    createdCount,
    attemptedCount: items.length,
    errors,
  };
}
